import { useState, useEffect, useCallback, type FormEvent } from 'react';
import {
    fetchExams as fetchExamsApi,
    createExam,
    updateExam,
    deleteExam as deleteExamApi,
    fetchExamSchedules as fetchExamSchedulesApi,
    type ExamApiRecord,
    type ExamScheduleApiRecord,
    type ExamType,
} from '../../services/examApi';
import type { ApiError } from '../../services/api';
import { getSchoolId, getAcademicSessionId } from '../../services/auth';
import ExamScheduleEditor from './ExamScheduleEditor';
import './ExamMaster.css';
import { useTranslation } from 'react-i18next';

// ── Helpers ─────────────────────────────────────────────────

function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatTime(t: string): string {
    const [h, m] = t.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
}

const EXAM_TYPE_LABELS: Record<ExamType, string> = {
    periodic: 'Periodic',
    midterm: 'Mid-Term',
    final: 'Final',
    supplementary: 'Supplementary',
};

// ── Types ───────────────────────────────────────────────────

type ExamDrawerMode = null | 'create' | 'edit';
type ExamFormErrors = {
    name?: string;
    start_date?: string;
    end_date?: string;
};
type ViewMode = 'list' | 'schedules';

// ── Component ───────────────────────────────────────────────

export default function ExamMaster() {
    const { t: _t } = useTranslation();

    // View state
    const [view, setView] = useState<ViewMode>('list');
    const [scheduleExam, setScheduleExam] = useState<ExamApiRecord | null>(null);

    // Exam list state
    const [exams, setExams] = useState<ExamApiRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [search, setSearch] = useState('');

    // Expanded exam → schedules (read-only preview)
    const [expandedExamId, setExpandedExamId] = useState<string | null>(null);
    const [schedules, setSchedules] = useState<ExamScheduleApiRecord[]>([]);
    const [schedulesLoading, setSchedulesLoading] = useState(false);

    // Exam drawer state
    const [examDrawerMode, setExamDrawerMode] = useState<ExamDrawerMode>(null);
    const [examEditingId, setExamEditingId] = useState<string | null>(null);
    const [examFormName, setExamFormName] = useState('');
    const [examFormType, setExamFormType] = useState<ExamType>('periodic');
    const [examFormStartDate, setExamFormStartDate] = useState('');
    const [examFormEndDate, setExamFormEndDate] = useState('');
    const [examFormIsActive, setExamFormIsActive] = useState(true);
    const [examErrors, setExamErrors] = useState<ExamFormErrors>({});
    const [examSubmitting, setExamSubmitting] = useState(false);
    const [examCreatedAt, setExamCreatedAt] = useState('');
    const [examUpdatedAt, setExamUpdatedAt] = useState('');

    // Delete modal
    const [deleteExamTarget, setDeleteExamTarget] = useState<ExamApiRecord | null>(null);

    // ── Load exams ──────────────────────────────────────────

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchExamsApi({ perPage: 50 });
                setExams(res.data);
            } catch (err) {
                setErrorMsg((err as ApiError).message || 'Failed to load exams');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    // ── Expand / collapse exam row (read-only preview) ──────

    const toggleExpand = useCallback(async (examId: string) => {
        if (expandedExamId === examId) {
            setExpandedExamId(null);
            setSchedules([]);
            return;
        }
        setExpandedExamId(examId);
        setSchedulesLoading(true);
        try {
            const res = await fetchExamSchedulesApi(examId, { perPage: 100 });
            setSchedules(res.data);
        } catch {
            setSchedules([]);
        } finally {
            setSchedulesLoading(false);
        }
    }, [expandedExamId]);

    // ── Filtered exams ──────────────────────────────────────

    const filtered = exams.filter((e) => {
        const q = search.toLowerCase();
        return e.name.toLowerCase().includes(q) || e.exam_type.toLowerCase().includes(q);
    });

    // ── Exam form helpers ───────────────────────────────────

    const resetExamForm = useCallback(() => {
        setExamFormName(''); setExamFormType('periodic');
        setExamFormStartDate(''); setExamFormEndDate('');
        setExamFormIsActive(true); setExamErrors({});
        setExamEditingId(null); setExamCreatedAt(''); setExamUpdatedAt('');
    }, []);

    const openExamCreate = () => {
        resetExamForm();
        setExamDrawerMode('create');
    };

    const openExamEdit = async (e: ExamApiRecord) => {
        resetExamForm();
        setExamFormName(e.name);
        setExamFormType(e.exam_type);
        setExamFormStartDate(e.start_date);
        setExamFormEndDate(e.end_date);
        setExamFormIsActive(e.is_active);
        setExamEditingId(e.id);
        setExamCreatedAt(e.created_at);
        setExamUpdatedAt(e.updated_at);
        setExamDrawerMode('edit');
    };

    const closeExamDrawer = () => { setExamDrawerMode(null); resetExamForm(); };

    const validateExam = (): boolean => {
        const e: ExamFormErrors = {};
        if (!examFormName.trim()) e.name = 'Exam name is required.';
        if (!examFormStartDate) e.start_date = 'Start date is required.';
        if (!examFormEndDate) e.end_date = 'End date is required.';
        else if (examFormStartDate && examFormEndDate < examFormStartDate) e.end_date = 'End date must be on or after start date.';
        setExamErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleExamSubmit = async (ev: FormEvent) => {
        ev.preventDefault();
        if (!validateExam()) return;
        setExamSubmitting(true);

        const payload = {
            school_id: getSchoolId() ?? '',
            academic_session_id: getAcademicSessionId() ?? '',
            name: examFormName.trim(),
            exam_type: examFormType,
            start_date: examFormStartDate,
            end_date: examFormEndDate,
            is_active: examFormIsActive,
        };

        try {
            if (examDrawerMode === 'edit' && examEditingId) {
                const updated = await updateExam(examEditingId, payload);
                setExams((prev) => prev.map((e) => e.id === examEditingId ? updated : e));
                showSuccess('Exam updated successfully!');
            } else {
                const created = await createExam(payload);
                setExams((prev) => [...prev, created]);
                showSuccess('Exam created successfully!');
            }
            closeExamDrawer();
        } catch (err) {
            setErrorMsg((err as ApiError).message || 'Failed to save exam');
        } finally {
            setExamSubmitting(false);
        }
    };

    const confirmDeleteExam = async () => {
        if (!deleteExamTarget) return;
        try {
            await deleteExamApi(deleteExamTarget.id);
            setExams((prev) => prev.filter((e) => e.id !== deleteExamTarget.id));
            if (expandedExamId === deleteExamTarget.id) {
                setExpandedExamId(null);
                setSchedules([]);
            }
            setDeleteExamTarget(null);
            showSuccess('Exam removed successfully!');
        } catch (err) {
            setDeleteExamTarget(null);
            setErrorMsg((err as ApiError).message || 'Failed to remove exam');
        }
    };

    // ── Navigate to schedule editor ─────────────────────────

    const openScheduleEditor = (exam: ExamApiRecord) => {
        setScheduleExam(exam);
        setView('schedules');
    };

    const backToList = () => {
        setView('list');
        setScheduleExam(null);
    };

    // ── Toasts ──────────────────────────────────────────────

    const showSuccess = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    // ── Schedule editor view ────────────────────────────────

    if (view === 'schedules' && scheduleExam) {
        return (
            <ExamScheduleEditor
                exam={scheduleExam}
                onBack={backToList}
                onSuccess={showSuccess}
            />
        );
    }

    // ── Exam list view ──────────────────────────────────────

    return (
        <div className="exams">
            {/* Toasts */}
            {successMsg && (
                <div className="exams__toast">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="exams__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && (
                <div className="exams__toast exams__toast--error">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    {errorMsg}
                    <button className="exams__toast-dismiss" onClick={() => setErrorMsg('')}>&times;</button>
                </div>
            )}

            {/* Header */}
            <div className="exams__header">
                <div className="exams__header-text">
                    <h1 className="exams__title">Examinations</h1>
                    <p className="exams__subtitle">Manage exams and their schedules. Click a row to preview schedules.</p>
                </div>
                <div className="exams__header-actions">
                    <div className="exams__search-wrap">
                        <svg className="exams__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input className="exams__search" type="text" placeholder="Search exams…" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <button className="exams__add-btn" onClick={openExamCreate}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        Add Exam
                    </button>
                </div>
            </div>

            {/* Exam Table */}
            <div className="exams__table-wrap">
                {loading ? (
                    <div className="exams__loading"><div className="exams__loader"><span className="exams__loader-dot" /><span className="exams__loader-dot" /><span className="exams__loader-dot" /></div></div>
                ) : filtered.length === 0 ? (
                    <div className="exams__empty">
                        <div className="exams__empty-icon"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg></div>
                        <h3 className="exams__empty-title">{search ? 'No exams match' : 'No exams yet'}</h3>
                        <p className="exams__empty-text">{search ? 'Try adjusting your search.' : 'Add your first exam to begin.'}</p>
                    </div>
                ) : (
                    <table className="exams__table">
                        <thead>
                            <tr>
                                <th className="exams__th exams__th--expand" />
                                <th className="exams__th">Exam Name</th>
                                <th className="exams__th">Type</th>
                                <th className="exams__th">Duration</th>
                                <th className="exams__th">Schedules</th>
                                <th className="exams__th">Status</th>
                                <th className="exams__th exams__th--actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((e) => {
                                const isExpanded = expandedExamId === e.id;
                                return (
                                    <ExamRow
                                        key={e.id}
                                        exam={e}
                                        isExpanded={isExpanded}
                                        schedules={isExpanded ? schedules : []}
                                        schedulesLoading={isExpanded && schedulesLoading}
                                        onToggle={() => toggleExpand(e.id)}
                                        onEditExam={() => openExamEdit(e)}
                                        onDeleteExam={() => setDeleteExamTarget(e)}
                                        onManageSchedules={() => openScheduleEditor(e)}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ── Exam Drawer ───────────────────────────────── */}
            {examDrawerMode && (
                <>
                    <div className="exams__overlay" onClick={closeExamDrawer} />
                    <aside className="exams__drawer">
                        <div className="exams__drawer-header">
                            <h2 className="exams__drawer-title">{examDrawerMode === 'edit' ? 'Edit Exam' : 'Add Exam'}</h2>
                            <button className="exams__drawer-close" onClick={closeExamDrawer} title="Close"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg></button>
                        </div>
                        <form className="exams__drawer-body" onSubmit={handleExamSubmit} noValidate id="exam-form">
                            <div className="exams__card">
                                <h3 className="exams__card-title">Exam Details</h3>
                                <div className="exams__field">
                                    <label className="exams__label exams__label--required" htmlFor="examName">Exam Name</label>
                                    <input className={`exams__input ${examErrors.name ? 'exams__input--error' : ''}`} type="text" id="examName" placeholder="e.g. Unit Test 1" maxLength={150} value={examFormName} onChange={(ev) => { setExamFormName(ev.target.value); if (examErrors.name) setExamErrors((p) => ({ ...p, name: undefined })); }} autoFocus />
                                    {examErrors.name && <p className="exams__error">{examErrors.name}</p>}
                                </div>
                                <div className="exams__field">
                                    <label className="exams__label exams__label--required" htmlFor="examType">Exam Type</label>
                                    <select className="exams__input exams__select" id="examType" value={examFormType} onChange={(ev) => setExamFormType(ev.target.value as ExamType)}>
                                        <option value="periodic">Periodic</option>
                                        <option value="midterm">Mid-Term</option>
                                        <option value="final">Final</option>
                                        <option value="supplementary">Supplementary</option>
                                    </select>
                                </div>
                            </div>

                            <div className="exams__card">
                                <h3 className="exams__card-title">Schedule</h3>
                                <div className="exams__form-row">
                                    <div className="exams__field">
                                        <label className="exams__label exams__label--required" htmlFor="examStartDate">Start Date</label>
                                        <input className={`exams__input ${examErrors.start_date ? 'exams__input--error' : ''}`} type="date" id="examStartDate" value={examFormStartDate} onChange={(ev) => { setExamFormStartDate(ev.target.value); if (examErrors.start_date) setExamErrors((p) => ({ ...p, start_date: undefined })); }} />
                                        {examErrors.start_date && <p className="exams__error">{examErrors.start_date}</p>}
                                    </div>
                                    <div className="exams__field">
                                        <label className="exams__label exams__label--required" htmlFor="examEndDate">End Date</label>
                                        <input className={`exams__input ${examErrors.end_date ? 'exams__input--error' : ''}`} type="date" id="examEndDate" value={examFormEndDate} min={examFormStartDate || undefined} onChange={(ev) => { setExamFormEndDate(ev.target.value); if (examErrors.end_date) setExamErrors((p) => ({ ...p, end_date: undefined })); }} />
                                        {examErrors.end_date && <p className="exams__error">{examErrors.end_date}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="exams__card">
                                <h3 className="exams__card-title">Settings</h3>
                                <div className="exams__toggle-row">
                                    <div className="exams__toggle-info"><span className="exams__toggle-label">Active</span><span className="exams__toggle-desc">Inactive exams are hidden from marks entry</span></div>
                                    <label className="exams__toggle"><input type="checkbox" checked={examFormIsActive} onChange={(ev) => setExamFormIsActive(ev.target.checked)} /><span className="exams__toggle-track" /></label>
                                </div>
                            </div>

                            {examDrawerMode === 'edit' && examCreatedAt && (
                                <div className="exams__timestamps">
                                    <span className="exams__timestamp">Created: {formatTimestamp(examCreatedAt)}</span>
                                    <span className="exams__timestamp">Updated: {formatTimestamp(examUpdatedAt)}</span>
                                </div>
                            )}
                        </form>
                        <div className="exams__drawer-footer">
                            <button className="exams__submit" type="submit" form="exam-form" disabled={examSubmitting}>
                                {examSubmitting ? (<><svg className="exams__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>{examDrawerMode === 'edit' ? 'Updating…' : 'Creating…'}</>) : examDrawerMode === 'edit' ? 'Update Exam' : 'Create Exam'}
                            </button>
                            <button className="exams__cancel" type="button" onClick={closeExamDrawer} disabled={examSubmitting}>Cancel</button>
                        </div>
                    </aside>
                </>
            )}

            {/* ── Delete Exam Confirm ───────────────────────── */}
            {deleteExamTarget && (
                <div className="exams__confirm-overlay" onClick={() => setDeleteExamTarget(null)}>
                    <div className="exams__confirm-modal" onClick={(ev) => ev.stopPropagation()}>
                        <h3 className="exams__confirm-title">Remove Exam</h3>
                        <p className="exams__confirm-text">Are you sure you want to remove <strong>{deleteExamTarget.name}</strong>? All associated schedules will also be removed.</p>
                        <div className="exams__confirm-actions">
                            <button className="exams__confirm-cancel" onClick={() => setDeleteExamTarget(null)}>Cancel</button>
                            <button className="exams__confirm-delete" onClick={confirmDeleteExam}>Remove</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── ExamRow sub-component ───────────────────────────────────

type ExamRowProps = {
    exam: ExamApiRecord;
    isExpanded: boolean;
    schedules: ExamScheduleApiRecord[];
    schedulesLoading: boolean;
    onToggle: () => void;
    onEditExam: () => void;
    onDeleteExam: () => void;
    onManageSchedules: () => void;
};

function ExamRow({ exam, isExpanded, schedules, schedulesLoading, onToggle, onEditExam, onDeleteExam, onManageSchedules }: ExamRowProps) {
    return (
        <>
            <tr className={`exams__table-row ${isExpanded ? 'exams__table-row--expanded' : ''}`} onClick={onToggle} style={{ cursor: 'pointer' }}>
                <td className="exams__td exams__td--expand">
                    <svg className={`exams__chevron ${isExpanded ? 'exams__chevron--open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </td>
                <td className="exams__td exams__td--name">
                    {exam.name}
                    {exam.academic_session?.name && <span className="exams__td-meta">{exam.academic_session.name}</span>}
                </td>
                <td className="exams__td">
                    <span className={`exams__badge exams__badge--${exam.exam_type}`}>
                        {EXAM_TYPE_LABELS[exam.exam_type]}
                    </span>
                </td>
                <td className="exams__td exams__td--date">{formatDate(exam.start_date)} – {formatDate(exam.end_date)}</td>
                <td className="exams__td">
                    <span className="exams__schedule-count">{exam.schedules_count ?? 0}</span>
                </td>
                <td className="exams__td">
                    <span className={`exams__badge ${exam.is_active ? 'exams__badge--active' : 'exams__badge--inactive'}`}>
                        {exam.is_active ? 'Active' : 'Inactive'}
                    </span>
                </td>
                <td className="exams__td exams__td--actions" onClick={(ev) => ev.stopPropagation()}>
                    <button className="exams__row-btn exams__row-btn--schedule" onClick={onManageSchedules} title="Manage schedules">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                        Schedules
                    </button>
                    <button className="exams__row-btn" onClick={onEditExam} title="Edit exam">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                    <button className="exams__row-btn exams__row-btn--delete" onClick={onDeleteExam} title="Remove exam">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                    </button>
                </td>
            </tr>

            {/* Read-only schedule preview */}
            {isExpanded && (
                <tr className="exams__expand-row">
                    <td colSpan={7} className="exams__expand-cell">
                        <div className="exams__schedules-panel">
                            <div className="exams__schedules-header">
                                <h4 className="exams__schedules-title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                    Schedules
                                    <span className="exams__schedules-count">{schedules.length}</span>
                                </h4>
                                <button className="exams__add-schedule-btn" onClick={onManageSchedules}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                    Manage Schedules
                                </button>
                            </div>

                            {schedulesLoading ? (
                                <div className="exams__schedules-loading"><span className="exams__loader-dot" /><span className="exams__loader-dot" /><span className="exams__loader-dot" /></div>
                            ) : schedules.length === 0 ? (
                                <div className="exams__schedules-empty">
                                    <p>No schedules yet. Click "Manage Schedules" to add subject-wise timetable.</p>
                                </div>
                            ) : (
                                <table className="exams__schedule-table">
                                    <thead>
                                        <tr>
                                            <th className="exams__sch-th">Subject</th>
                                            <th className="exams__sch-th">Class / Section</th>
                                            <th className="exams__sch-th">Date</th>
                                            <th className="exams__sch-th">Time</th>
                                            <th className="exams__sch-th">Room</th>
                                            <th className="exams__sch-th">Marks</th>
                                            <th className="exams__sch-th">Invigilator</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schedules.map((s) => (
                                            <tr key={s.id} className="exams__schedule-row">
                                                <td className="exams__sch-td exams__sch-td--subject">{s.subject?.board_subject_name ?? '—'}</td>
                                                <td className="exams__sch-td">{s.school_class?.name ?? '—'}{s.section?.name ? ` — ${s.section.name}` : ''}</td>
                                                <td className="exams__sch-td exams__sch-td--date">{formatDate(s.exam_date)}</td>
                                                <td className="exams__sch-td exams__sch-td--time">{formatTime(s.start_time)} – {formatTime(s.end_time)}</td>
                                                <td className="exams__sch-td">{s.room_number ?? '—'}</td>
                                                <td className="exams__sch-td">
                                                    <span className="exams__marks">{s.max_marks}</span>
                                                    <span className="exams__marks-sep">/</span>
                                                    <span className="exams__marks exams__marks--pass">{s.pass_marks}</span>
                                                </td>
                                                <td className="exams__sch-td">{s.invigilator ? `${s.invigilator.first_name} ${s.invigilator.last_name}` : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
